import { useCallback, useEffect, useRef, useState } from 'react';

import { IExternalPricesResponse } from 'parts/types';
import { PricingHelper } from 'pricing/helpers';

export enum ActiveCostType {
  REPLACEMENT = 'replacement',
  SPA = 'spa',
}

const PRICE_DECIMALS = 2;

interface IProps {
  externalPricing?: IExternalPricesResponse | null;
  replacementStandardCost?: number | null;
  spaStandardCost?: number | null;
}

export const useSellingPriceState = ({
  externalPricing,
  replacementStandardCost,
  spaStandardCost,
}: IProps) => {
  const [activeCostType, setActiveCostTypeState] = useState<ActiveCostType>(() =>
    spaStandardCost ? ActiveCostType.SPA : ActiveCostType.REPLACEMENT
  );
  const [price, setPriceState] = useState<number | null>(null);
  const [pricingMargin, setPricingMarginState] = useState<number | null>(null);

  const isSpaAvailable = !!spaStandardCost;
  const isReplacementAvailable = !!replacementStandardCost;
  const showRadio = isSpaAvailable && isReplacementAvailable;

  const activeCostTypeRef = useRef(activeCostType);
  const pricingMarginRef = useRef(pricingMargin);
  const userHasChosenTypeRef = useRef(false);
  const userHasEditedRef = useRef(false);

  const setActiveCostType = useCallback((newType: ActiveCostType) => {
    activeCostTypeRef.current = newType;
    setActiveCostTypeState(newType);
  }, []);

  const setPricingMargin = useCallback((newMargin: number | null) => {
    pricingMarginRef.current = newMargin;
    setPricingMarginState(newMargin);
  }, []);

  const activeCost =
    activeCostType === ActiveCostType.SPA ? spaStandardCost ?? null : replacementStandardCost;

  useEffect(() => {
    if (!externalPricing) {
      setPriceState(null);
      setPricingMargin(null);
      setActiveCostType(ActiveCostType.REPLACEMENT);
      userHasChosenTypeRef.current = false;
      userHasEditedRef.current = false;

      return;
    }

    let newType: ActiveCostType;

    if (userHasChosenTypeRef.current) {
      const currentType = activeCostTypeRef.current;
      const currentValid =
        (currentType === ActiveCostType.SPA && isSpaAvailable) ||
        (currentType === ActiveCostType.REPLACEMENT && isReplacementAvailable);

      newType = currentValid
        ? currentType
        : isSpaAvailable
        ? ActiveCostType.SPA
        : ActiveCostType.REPLACEMENT;
    } else {
      newType = isSpaAvailable ? ActiveCostType.SPA : ActiveCostType.REPLACEMENT;
    }

    setActiveCostType(newType);

    const newCost = newType === ActiveCostType.SPA ? spaStandardCost : replacementStandardCost;

    if (userHasEditedRef.current) {
      const margin = pricingMarginRef.current;
      const newPrice =
        newCost && margin != null
          ? PricingHelper.computeAndFormatPrice(newCost, margin / 100, { decimals: PRICE_DECIMALS })
          : null;

      setPriceState(newPrice);

      return;
    }

    const bePrice = externalPricing.price ?? null;
    const computedMargin =
      bePrice && newCost ? PricingHelper.computeGpmPercentage(newCost, bePrice) : null;

    setPriceState(bePrice);
    setPricingMargin(computedMargin);
  }, [
    externalPricing,
    isSpaAvailable,
    isReplacementAvailable,
    spaStandardCost,
    replacementStandardCost,
    setActiveCostType,
    setPricingMargin,
  ]);

  const onActiveCostTypeChange = useCallback(
    (newType: ActiveCostType) => {
      userHasChosenTypeRef.current = true;
      setActiveCostType(newType);

      const newCost = newType === ActiveCostType.SPA ? spaStandardCost : replacementStandardCost;
      const margin = pricingMarginRef.current;

      if (newCost && margin != null) {
        setPriceState(
          PricingHelper.computeAndFormatPrice(newCost, margin / 100, { decimals: PRICE_DECIMALS })
        );
      }
    },
    [spaStandardCost, replacementStandardCost, setActiveCostType]
  );

  const onPriceChange = useCallback(
    (newPrice: number | null) => {
      userHasEditedRef.current = true;
      setPriceState(newPrice);

      const cost =
        activeCostTypeRef.current === ActiveCostType.SPA
          ? spaStandardCost
          : replacementStandardCost;

      if (newPrice != null && cost) {
        setPricingMargin(PricingHelper.computeGpmPercentage(cost, newPrice));
      }
    },
    [spaStandardCost, replacementStandardCost, setPricingMargin]
  );

  const onMarginChange = useCallback(
    (newMargin: number | null) => {
      userHasEditedRef.current = true;
      setPricingMargin(newMargin);

      const cost =
        activeCostTypeRef.current === ActiveCostType.SPA
          ? spaStandardCost
          : replacementStandardCost;

      if (newMargin != null && cost) {
        setPriceState(
          PricingHelper.computeAndFormatPrice(cost, newMargin / 100, { decimals: PRICE_DECIMALS })
        );
      }
    },
    [spaStandardCost, replacementStandardCost, setPricingMargin]
  );

  return {
    activeCostType,
    activeCost,
    price,
    pricingMargin,
    showRadio,
    isSpaAvailable,
    isReplacementAvailable,
    onActiveCostTypeChange,
    onPriceChange,
    onMarginChange,
  };
};
